<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- ARIA dialog container requires keyboard handler; role="dialog" is semantically interactive -->
  <div
    class="fixed inset-0 z-50 flex"
    role="dialog"
    aria-modal="true"
    aria-labelledby="driver-profile-panel-title"
    @keydown.esc="emit('close')"
    @keydown.tab="trapFocus"
  >
    <!-- Backdrop -->
    <div
      class="fixed inset-0 bg-black/50"
      aria-hidden="true"
      @click="emit('close')"
    />

    <!-- Panel -->
    <aside
      class="relative ml-auto w-full max-w-sm bg-background border-l border-border shadow-xl flex flex-col h-full overflow-y-auto"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
        <h2
          id="driver-profile-panel-title"
          class="text-sm font-semibold text-foreground"
        >
          Driver Profile
        </h2>
        <button
          ref="closeBtn"
          class="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Close driver profile panel"
          @click="emit('close')"
        >
          <XIcon
            class="w-4 h-4"
            aria-hidden="true"
          />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 px-5 py-5 space-y-7">
        <!-- Section 1: Default mode -->
        <section aria-labelledby="default-mode-label">
          <h3
            id="default-mode-label"
            class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3"
          >
            Default mode at boot
          </h3>
          <p class="text-xs text-muted-foreground mb-3">
            Sets the starting dashboard mode when no manual override is active.
          </p>
          <select
            :value="profile.preferredDefaultMode ?? ''"
            class="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-labelledby="default-mode-label"
            @change="onDefaultModeChange"
          >
            <option value="">
              Auto (follow vehicle state)
            </option>
            <option value="driving">
              Driving
            </option>
            <option value="parked">
              Parked
            </option>
            <option value="charging">
              Charging
            </option>
            <option value="analytics">
              Analytics
            </option>
            <option value="trip-planning">
              Trip Planning
            </option>
          </select>
          <p
            v-if="mostVisitedMode !== null && mostVisitedMode !== profile.preferredDefaultMode"
            class="mt-2 text-xs text-muted-foreground flex items-center gap-2 flex-wrap"
          >
            Most visited mode: <strong>{{ modeLabel(mostVisitedMode) }}</strong>
            <button
              class="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs hover:bg-primary/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              @click="setProfile({ preferredDefaultMode: mostVisitedMode })"
            >
              Use as default
            </button>
          </p>
        </section>

        <!-- Section 2: Pinned / hidden analytics cards -->
        <section aria-labelledby="cards-label">
          <h3
            id="cards-label"
            class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3"
          >
            Analytics cards
          </h3>
          <p class="text-xs text-muted-foreground mb-3">
            Pin cards to show them first; hide them to remove from the analytics view.
          </p>
          <p
            v-if="mostViewedUnpinnedCard !== null"
            class="mb-3 text-xs text-muted-foreground flex items-center gap-2 flex-wrap"
          >
            Frequently viewed: <strong>{{ cardLabel(mostViewedUnpinnedCard) }}</strong>
            <button
              class="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs hover:bg-primary/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              @click="pinMostViewed()"
            >
              Pin it
            </button>
          </p>
          <ul class="space-y-2">
            <li
              v-for="card in CARD_DEFS"
              :key="card.id"
              class="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-secondary/30 border border-border"
            >
              <span class="text-sm text-foreground">{{ card.label }}</span>
              <div class="flex items-center gap-2">
                <!-- Pin toggle -->
                <button
                  :aria-pressed="isPinned(card.id)"
                  :aria-label="isPinned(card.id) ? `Unpin ${card.label}` : `Pin ${card.label}`"
                  :class="[
                    'p-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isPinned(card.id)
                      ? 'text-primary bg-primary/15 hover:bg-primary/25'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
                  ]"
                  :disabled="isHidden(card.id)"
                  @click="togglePin(card.id)"
                >
                  <PinIcon
                    class="w-3.5 h-3.5"
                    aria-hidden="true"
                  />
                </button>
                <!-- Hide toggle -->
                <button
                  :aria-pressed="isHidden(card.id)"
                  :aria-label="isHidden(card.id) ? `Show ${card.label}` : `Hide ${card.label}`"
                  :class="[
                    'p-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isHidden(card.id)
                      ? 'text-destructive bg-destructive/15 hover:bg-destructive/25'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
                  ]"
                  @click="toggleHide(card.id)"
                >
                  <EyeOffIcon
                    class="w-3.5 h-3.5"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </li>
          </ul>
        </section>

        <!-- Section 3: Theme accent -->
        <section aria-labelledby="theme-accent-label">
          <h3
            id="theme-accent-label"
            class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3"
          >
            Theme accent
          </h3>
          <fieldset aria-labelledby="theme-accent-label">
            <legend class="sr-only">
              Choose theme accent
            </legend>
            <div class="space-y-2">
              <label
                v-for="accent in ACCENT_OPTIONS"
                :key="accent.value"
                class="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="radio"
                  name="theme-accent"
                  :value="accent.value"
                  :checked="profile.themeAccent === accent.value"
                  class="accent-primary"
                  @change="setProfile({ themeAccent: accent.value })"
                >
                <span class="text-sm text-foreground">{{ accent.label }}</span>
                <span class="text-xs text-muted-foreground">{{ accent.description }}</span>
              </label>
            </div>
          </fieldset>
        </section>

        <!-- Section 4: Energy unit -->
        <section aria-labelledby="energy-unit-label">
          <h3
            id="energy-unit-label"
            class="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3"
          >
            Energy unit
          </h3>
          <p class="text-xs text-muted-foreground mb-3">
            Used in efficiency charts and stats.
          </p>
          <fieldset aria-labelledby="energy-unit-label">
            <legend class="sr-only">
              Choose energy unit
            </legend>
            <div class="space-y-2">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="energy-unit"
                  value="kwh"
                  :checked="profile.preferredEnergyUnit === 'kwh'"
                  class="accent-primary"
                  @change="setProfile({ preferredEnergyUnit: 'kwh' })"
                >
                <span class="text-sm text-foreground">kWh / 100 km</span>
              </label>
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="energy-unit"
                  value="wh_per_km"
                  :checked="profile.preferredEnergyUnit === 'wh_per_km'"
                  class="accent-primary"
                  @change="setProfile({ preferredEnergyUnit: 'wh_per_km' })"
                >
                <span class="text-sm text-foreground">Wh / km</span>
              </label>
            </div>
          </fieldset>
        </section>

        <!-- Reset + Sign out -->
        <div class="pt-2 border-t border-border space-y-2">
          <button
            class="w-full px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            @click="onReset"
          >
            Reset to defaults
          </button>
          <button
            class="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            @click="emit('sign-out')"
          >
            <LogOutIcon
              class="w-4 h-4"
              aria-hidden="true"
            />
            Sign out
          </button>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { XIcon, PinIcon, EyeOffIcon, LogOutIcon } from '@lucide/vue'
import { useDriverProfile } from '@/composables/useDriverProfile'
import type { DashboardMode } from '@/composables/useDashboardMode'

const emit = defineEmits<{ close: []; 'sign-out': [] }>()

const closeBtn = ref<HTMLElement | null>(null)
let focusRestoreTarget: Element | null = null

onMounted(async () => {
  focusRestoreTarget = document.activeElement
  await nextTick()
  closeBtn.value?.focus()
})

onUnmounted(() => {
  ;(focusRestoreTarget as HTMLElement | null)?.focus()
})

function trapFocus(event: KeyboardEvent): void {
  const dialog = event.currentTarget as HTMLElement
  const focusables = Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"])',
    ),
  )
  if (focusables.length === 0) return

  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement

  if (event.shiftKey) {
    if (active === first) {
      event.preventDefault()
      last.focus()
    }
  } else {
    if (active === last) {
      event.preventDefault()
      first.focus()
    }
  }
}

const { profile: profileRef, mostVisitedMode, mostViewedUnpinnedCard, setProfile, resetProfile } = useDriverProfile()
const profile = computed(() => profileRef.value)

const MODE_LABELS: Record<string, string> = {
  driving: 'Driving',
  parked: 'Parked',
  charging: 'Charging',
  analytics: 'Analytics',
  'trip-planning': 'Trip Planning',
}

function modeLabel(mode: string): string {
  return MODE_LABELS[mode] ?? mode
}

function cardLabel(id: string | null): string {
  if (!id) return ''
  return CARD_DEFS.find((c) => c.id === id)?.label ?? id
}

function pinMostViewed(): void {
  const id = mostViewedUnpinnedCard.value
  if (id !== null) togglePin(id)
}

const CARD_DEFS = [
  { id: 'weekly', label: 'Weekly Insights' },
  { id: 'lifetime', label: 'Lifetime Stats' },
  { id: 'range-forecast', label: 'Range Forecast' },
  { id: 'whatif', label: 'What-If Simulator' },
  { id: 'anomalies', label: 'Anomalies' },
  { id: 'efficiency-trend', label: 'Efficiency Trend' },
  { id: 'efficiency-calendar', label: 'Efficiency Calendar' },
  { id: 'recent-activity', label: 'Recent Activity' },
] as const

const ACCENT_OPTIONS: { value: 'neon' | 'classic' | 'minimal'; label: string; description: string }[] = [
  { value: 'classic', label: 'Classic', description: 'Hyundai blue' },
  { value: 'neon', label: 'Neon', description: 'Electric teal glow' },
  { value: 'minimal', label: 'Minimal', description: 'Muted neutrals' },
]

function isPinned(id: string): boolean {
  return profile.value.pinnedCards.includes(id)
}

function isHidden(id: string): boolean {
  return profile.value.hiddenCards.includes(id)
}

function togglePin(id: string): void {
  const pinned = profile.value.pinnedCards
  if (pinned.includes(id)) {
    setProfile({ pinnedCards: pinned.filter((c) => c !== id) })
  } else {
    setProfile({ pinnedCards: [...pinned, id] })
  }
}

function toggleHide(id: string): void {
  const hidden = profile.value.hiddenCards
  const pinned = profile.value.pinnedCards
  if (hidden.includes(id)) {
    setProfile({ hiddenCards: hidden.filter((c) => c !== id) })
  } else {
    setProfile({
      hiddenCards: [...hidden, id],
      pinnedCards: pinned.filter((c) => c !== id),
    })
  }
}

function onDefaultModeChange(event: Event): void {
  const val = (event.target as HTMLSelectElement).value
  const mode: DashboardMode | null =
    val === 'driving' || val === 'parked' || val === 'charging' || val === 'analytics' || val === 'trip-planning' ? val : null
  setProfile({ preferredDefaultMode: mode })
}

function onReset(): void {
  resetProfile()
}
</script>
